/**
 * SaasHome — §2 "Linear/Vercel professional tool" dashboard variant.
 *
 * Layout:
 *   Row 1: 4 borderless metric cells in a hairline-divided grid
 *          (big tabular-nums, tiny muted label, NO card/icon tiles)
 *   Row 2: [3/5] events-per-day BarMini card (14 days)
 *          [2/5] compact activity list
 *   Row 3: map card full-width h-72 (anchor inside)
 *
 * Data contract: receives ONLY what DashboardPage already computed — NO new fetches.
 */

import { Activity, MapPin, WifiOff } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Event, CameraMapPin } from '@/types'
import { eventsPerDay } from '@/lib/eventsPerDay'
import { BarMini } from '@/components/charts/BarMini'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { formatRelativeTime } from '@/lib/utils'

export interface SaasHomeProps {
  cameras: { id: number; is_active: boolean }[]
  totalCameras: number
  camerasLoading: boolean
  events: Event[]
  totalEvents: number
  eventsLoading: boolean
  mapPins: CameraMapPin[]
  setAnchor: (el: HTMLDivElement | null) => void
}

// ── SaasHome ─────────────────────────────────────────────────────────────────

export function SaasHome({
  cameras,
  totalCameras,
  camerasLoading,
  events,
  totalEvents,
  eventsLoading,
  mapPins,
  setAnchor,
}: SaasHomeProps) {
  const { t } = useTranslation('cameras')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')

  const activeCameras = camerasLoading ? 0 : cameras.filter(c => c.is_active).length
  const onlineCameras = mapPins.filter(p => p.is_online).length

  // 14-day series for events-per-day chart — bucket by occurred_at (what rows display)
  const eventSeries = eventsPerDay(events.map(e => ({ created_at: e.occurred_at })), 14, new Date())

  return (
    <div className="space-y-4">

      {/* ── Metrics row — borderless hairline-divided cells ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px lg:divide-x lg:gap-0 divide-border border border-border rounded-md overflow-hidden bg-card">
        {[
          {
            label: t('dashboard.totalCameras'),
            value: totalCameras,
            loading: camerasLoading,
          },
          {
            label: t('dashboard.detectionEvents'),
            value: totalEvents,
            loading: eventsLoading,
          },
          {
            label: t('dashboard.activeCameras'),
            value: activeCameras,
            loading: camerasLoading,
          },
          {
            label: tc('status.online', { defaultValue: 'Online' }),
            value: onlineCameras,
            loading: false,
          },
        ].map(({ label, value, loading }) => (
          <div key={label} className="px-5 py-4 flex flex-col gap-1">
            {loading ? (
              <>
                <div className="shimmer h-7 w-12 rounded mb-0.5" />
                <div className="shimmer h-3 w-20 rounded" />
              </>
            ) : (
              <>
                <span className="text-2xl font-semibold tabular-nums text-foreground leading-none">
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </span>
                <span className="text-[12px] text-muted-foreground leading-tight">{label}</span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* ── Middle row: events chart (3/5) + activity list (2/5) ── */}
      <div className="grid gap-4 lg:grid-cols-5">

        {/* Events-per-day chart card */}
        <Card className="lg:col-span-3 flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[13px] font-medium text-foreground">
                {te('eventsPerDay')}
              </CardTitle>
              <span className="text-[12px] text-muted-foreground">{te('eventsPerDayRange', { count: 14 })}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-0 pb-4 flex-1 flex flex-col justify-end">
            {eventsLoading ? (
              <div className="shimmer h-20 w-full rounded" />
            ) : (
              <BarMini data={eventSeries} className="h-20 w-full" />
            )}
          </CardContent>
        </Card>

        {/* Compact activity list */}
        <Card className="lg:col-span-2 flex flex-col overflow-hidden h-[200px] lg:h-auto">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[13px] font-medium">{te('title', { defaultValue: 'Recent Events' })}</CardTitle>
              {!eventsLoading && totalEvents > 0 && (
                <Link
                  to="/events"
                  className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {te('viewAll', { defaultValue: 'View all →' })}
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto min-h-0">
            {eventsLoading ? (
              <div className="space-y-0 px-4 pb-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-border/50">
                    <div className="shimmer h-3 w-32 rounded" />
                    <div className="shimmer h-3 w-16 rounded ml-auto" />
                  </div>
                ))}
              </div>
            ) : events.length > 0 ? (
              <div className="divide-y divide-border">
                {events.slice(0, 10).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-muted transition-colors duration-100 text-[13px]"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-foreground/80 truncate block leading-tight">{event.camera_name}</span>
                      {event.detected_objects?.length > 0 && (
                        <span className="text-[11px] text-muted-foreground truncate block leading-tight">
                          {event.detected_objects.slice(0, 2).map(o =>
                            tc(`detectionClasses.${o}`, { defaultValue: o.replace(/_/g, ' ') })
                          ).join(', ')}
                          {event.detected_objects.length > 2 ? ` +${event.detected_objects.length - 2}` : ''}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                      {formatRelativeTime(event.occurred_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-1 p-4 text-center">
                <Activity className="h-5 w-5 text-muted-foreground/30" />
                <span className="text-[13px] text-muted-foreground">{te('noRecentEvents', { defaultValue: 'No events yet' })}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Map card — full width, h-72 ── */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-[13px] font-medium">{t('map.title', { defaultValue: 'Camera Map' })}</CardTitle>
            {mapPins.length > 0 && (
              <span className="ml-auto text-[12px] text-muted-foreground">
                {t('map.mapped', { count: mapPins.length })}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 h-72 overflow-hidden">
          {mapPins.length > 0 ? (
            <div
              ref={setAnchor}
              className="h-full min-h-0 w-full overflow-hidden rounded-b-lg"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-center p-4">
              <WifiOff className="h-7 w-7 text-muted-foreground/20" />
              <span className="text-[13px] text-muted-foreground">{t('map.noCoordinates', { defaultValue: 'No camera coordinates' })}</span>
              <Link to="/cameras" className="text-[12px] text-muted-foreground hover:text-foreground transition-colors">
                {t('map.goToCameras', { defaultValue: 'Go to Cameras →' })}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
